import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Copy, Users, Check, Shuffle } from "lucide-react";
import { toast } from "sonner";
import SituationPath from "@/components/relations/SituationPath";
import PeopleMap from "@/components/relations/PeopleMap";
import RelationsHero from "@/components/relations/RelationsHero";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";
import { SITUATIONS, SCRIPTS, TONES, WEEKLY, LONELY_BARS, ASKED } from "@/lib/relationsData";

const PAPER = "#FCF6EE";
const INK = "#33251A";
const SOFT = "rgba(51,37,26,0.62)";
const AMBER = "#E89A4F";

/* --------------------------------------------- the wall of sentences */
function Wall({ onPick }) {
  const [hover, setHover] = useState(null);
  return (
    <div className="w-full" data-testid="situations-wall">
      {SITUATIONS.map((s, i) => (
        <motion.button key={s.key} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => onPick(s.key)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
          className="group block w-full text-left py-4 sm:py-5 relative"
          style={{ borderTop: "1px solid rgba(51,37,26,0.1)", paddingLeft: i % 3 === 1 ? 28 : i % 3 === 2 ? 56 : 0, opacity: hover === null || hover === i ? 1 : 0.42, transition: "opacity 0.35s, padding 0.4s" }}
          data-testid={`situation-${s.key}`}>
          <div className="flex items-baseline gap-4">
            <span className="font-display text-[26px] sm:text-[38px] lg:text-[44px] leading-[1.06] flex-1" style={{ color: hover === i ? s.tone : INK, transition: "color 0.3s" }}>
              {s.say}
            </span>
            <ArrowRight size={20} className="shrink-0 mb-1 transition-all" style={{ color: s.tone, opacity: hover === i ? 1 : 0, transform: hover === i ? "translateX(0)" : "translateX(-10px)" }} />
          </div>
          <span className="block text-[12.5px] mt-1.5" style={{ color: "rgba(51,37,26,0.42)" }}>{s.who}</span>
        </motion.button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------- the scripts */
function ScriptsBand() {
  const [i, setI] = useState(0);
  const [tone, setTone] = useState("plain");
  const s = SCRIPTS[i];
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #33251A, #241A12 72%)" }} data-testid="relations-scripts">
      <span className="absolute right-[8%] -top-28 w-[34vw] h-[34vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,154,79,0.34), transparent 68%)", filter: "blur(44px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(232,154,79,0.16)", color: AMBER }}>
            <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: AMBER }} />six conversations that stall in the first two sentences
          </span>
          <h2 className="font-display text-[34px] sm:text-[46px] leading-[1.02] mt-4 text-white">The scripts</h2>
          <p className="text-[15px] mt-3 max-w-sm text-white/55">Written to be stolen. Pick the temperature, copy it, change every word if you like.</p>
          <div className="mt-7 space-y-1.5">
            {SCRIPTS.map((x, n) => (
              <button key={x.t} onClick={() => setI(n)} className="w-full text-left rounded-2xl px-5 py-3.5 transition-all hover:translate-x-1"
                style={i === n ? { background: "rgba(255,255,255,0.1)", border: `1px solid ${AMBER}55` } : { background: "rgba(255,255,255,0.045)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`script-${n}`}>
                <p className="font-display text-[16.5px] leading-tight text-white/90">{x.t}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-[2rem] p-7 sm:p-9 h-full" style={{ background: "rgba(255,255,255,0.055)", border: "1px solid rgba(255,255,255,0.11)", backdropFilter: "blur(14px)" }}>
            <p className="text-[13px]" style={{ color: AMBER }}>{s.hint}</p>
            <div className="flex gap-1.5 mt-5 p-1 rounded-full max-w-[280px]" style={{ background: "rgba(255,255,255,0.07)" }}>
              {TONES.map((t) => (
                <button key={t.k} onClick={() => setTone(t.k)} className="flex-1 rounded-full py-2 text-[12.5px] font-semibold transition-colors"
                  style={tone === t.k ? { background: AMBER, color: "#33251A" } : { color: "rgba(255,255,255,0.5)" }} data-testid={`script-tone-${t.k}`}>{t.label}</button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.p key={`${i}-${tone}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.32 }}
                className="font-display text-[22px] sm:text-[27px] leading-[1.45] mt-7 text-white" data-testid="script-text">“{s[tone]}”</motion.p>
            </AnimatePresence>
            <button onClick={() => { navigator.clipboard?.writeText(s[tone]); toast("Copied."); }}
              className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold" style={{ background: AMBER, color: "#33251A" }} data-testid="script-copy">
              <Copy size={13} /> Copy it
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- one outward action */
function Weekly({ log, onLog }) {
  const [pick, setPick] = useState(() => Math.floor(Math.random() * WEEKLY.length));
  const list = log || [];
  const current = WEEKLY[pick];
  const already = list.includes(current);
  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #FCF6EE, #F7EBDA)" }} data-testid="relations-weekly">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(51,37,26,0.4)" }}>One outward action</p>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2" style={{ color: INK }}>One a week, chosen by you, logged by nobody else</h2>
          <p className="text-[15px] mt-4 max-w-md" style={{ color: SOFT }}>Connection does not improve because you understand it. It improves because of small outward moves that are slightly uncomfortable.</p>
          <div className="flex flex-wrap gap-1.5 mt-7">
            {Array.from({ length: 12 }, (_, i) => (
              <span key={i} className="w-6 h-6 rounded-lg" style={{ background: i < list.length ? AMBER : "rgba(51,37,26,0.07)" }} data-testid={`weekly-dot-${i}`} />
            ))}
            <span className="text-[12.5px] self-center ml-2" style={{ color: "rgba(51,37,26,0.45)" }}>{list.length} logged</span>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-[2rem] p-7 sm:p-9 relative overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(51,37,26,0.08)", boxShadow: "0 44px 90px -70px rgba(51,37,26,0.9)" }}>
            <span className="absolute -right-14 -top-14 w-44 h-44 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,154,79,0.3), transparent 70%)" }} />
            <p className="relative text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: AMBER }}>this week</p>
            <AnimatePresence mode="wait">
              <motion.p key={pick} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.32 }}
                className="relative font-display text-[26px] sm:text-[34px] leading-tight mt-3" style={{ color: INK }} data-testid="weekly-current">{current}</motion.p>
            </AnimatePresence>
            <div className="relative flex flex-wrap items-center gap-3 mt-7">
              <button onClick={() => onLog(current)} disabled={already} className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-45"
                style={{ background: INK }} data-testid="weekly-done">
                {already ? <><Check size={14} /> Logged</> : <><Check size={14} /> I did it</>}
              </button>
              <button onClick={() => setPick((p) => (p + 1) % WEEKLY.length)} className="inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold"
                style={{ background: "rgba(51,37,26,0.06)", color: INK }} data-testid="weekly-shuffle">
                <Shuffle size={13} /> Give me another
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------- loneliness, sized */
function Lonely() {
  const [on, setOn] = useState(0);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #F7EBDA, #FCF6EE)" }} data-testid="relations-lonely">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(51,37,26,0.4)" }}>Sized against things we do talk about</p>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2" style={{ color: INK }}>Loneliness is a health risk, and it is treated as a mood</h2>
          <AnimatePresence mode="wait">
            <motion.p key={on} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-[15px] mt-5 max-w-md" style={{ color: SOFT }}>
              {LONELY_BARS[on].note}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="lg:col-span-7 space-y-5">
          {LONELY_BARS.map((b, i) => (
            <button key={b.label} onMouseEnter={() => setOn(i)} onFocus={() => setOn(i)} onClick={() => setOn(i)} className="block w-full text-left" data-testid={`lonely-bar-${i}`}>
              <div className="flex items-baseline justify-between">
                <p className="text-[14.5px]" style={{ color: on === i ? INK : SOFT, fontWeight: on === i ? 600 : 400 }}>{b.label}</p>
                <p className="font-display text-[17px] tabular-nums" style={{ color: i === 0 ? "#C4553F" : "rgba(51,37,26,0.4)" }}>{b.v}</p>
              </div>
              <div className="h-3 rounded-full mt-2 overflow-hidden" style={{ background: "rgba(51,37,26,0.07)" }}>
                <motion.span className="block h-full rounded-full" initial={{ width: 0 }} whileInView={{ width: `${b.v}%` }} viewport={{ once: true }}
                  transition={{ duration: 1, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }} style={{ background: i === 0 ? "linear-gradient(90deg, #E89A4F, #C4553F)" : "rgba(51,37,26,0.22)" }} />
              </div>
            </button>
          ))}
          <p className="text-[12px] pt-1" style={{ color: "rgba(51,37,26,0.4)" }}>Relative effect on early death risk, drawn from meta analyses of social connection. Indicative, not a league table.</p>
        </div>
      </div>
    </section>
  );
}

function Asked() {
  const [on, setOn] = useState(0);
  return (
    <section style={{ background: PAPER }} data-testid="relations-asked">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(51,37,26,0.4)" }}>About this place</p>
        <h2 className="font-display text-[30px] sm:text-[40px] leading-[1.04] mt-2 mb-8" style={{ color: INK }}>Three things worth saying out loud</h2>
        <div className="space-y-2">
          {ASKED.map((e, i) => (
            <div key={e.q} className="rounded-2xl overflow-hidden" style={{ background: on === i ? "#fff" : "rgba(255,255,255,0.6)", border: "1px solid rgba(51,37,26,0.07)" }}>
              <button onClick={() => setOn(on === i ? -1 : i)} className="w-full flex items-center gap-4 px-6 py-4 text-left" data-testid={`relations-asked-${i}`}>
                <span className="font-display text-[18px] flex-1" style={{ color: INK }}>{e.q}</span>
                <ChevronDown size={15} style={{ color: "rgba(51,37,26,0.35)", transform: on === i ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
              </button>
              <AnimatePresence initial={false}>
                {on === i && (
                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-6 pb-5 text-[15px] leading-[1.85]" style={{ color: SOFT }}>{e.a}</motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Relationships() {
  const [data, patch] = useProgress("relations", { people: [], actions: {}, weekly: [] });
  const [openKey, setOpenKey] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "13%"]);
  const s = SITUATIONS.find((x) => x.key === openKey);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [openKey]);

  return (
    <main className="pt-16" style={{ background: PAPER }} data-testid="platform-page-relationships">
      <section ref={heroRef} className="relative overflow-hidden" data-testid="relations-stage">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #FDF7EF 0%, #FBF2E5 46%, #FCF6EE 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(44% 40% at 86% 4%, rgba(232,154,79,0.26), transparent 72%), radial-gradient(40% 40% at 0% 88%, rgba(181,106,34,0.16), transparent 72%)" }} />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-16">
          <AnimatePresence mode="wait">
            {!s ? (
              <motion.div key="wall" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -22, filter: "blur(8px)" }} transition={{ duration: 0.5 }}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-5">
                    <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] tracking-[0.28em] uppercase font-semibold" style={{ color: "rgba(51,37,26,0.6)" }}>Relationships and family · nobody searches for advice</motion.p>
                    <h1 className="font-display text-[34px] sm:text-[48px] lg:text-[56px] leading-[1.0] mt-5 max-w-xl" style={{ color: INK }}>
                      {[["Find", 0], ["the", 1], ["sentence", 2], ["\n", -1], ["you", 3], ["recognise.", 4]].map(([w, i], n) =>
                        w === "\n" ? <br key={n} /> : (
                          <motion.span key={n} initial={{ opacity: 0, y: 22, filter: "blur(7px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            transition={{ delay: 0.14 + i * 0.09, duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="inline-block mr-[0.24em]" style={{ color: i >= 3 ? "#B56A22" : INK }}>{w}</motion.span>
                        ))}
                    </h1>
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }} className="text-[15.5px] mt-5 max-w-md" style={{ color: SOFT }}>
                      Each one opens what is actually happening, the first move, and something you could send tonight.
                    </motion.p>
                  </div>
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-7 -mr-6" data-testid="relations-hero-animation">
                    <RelationsHero accent={AMBER} />
                  </motion.div>
                </div>
                <div className="mt-8"><Wall onPick={setOpenKey} /></div>
              </motion.div>
            ) : (
              <SituationPath key={s.key} s={s} done={!!(data.actions || {})[s.key]}
                onDone={(v) => patch((d) => ({ actions: { ...(d.actions || {}), [s.key]: v } }))}
                onBack={() => setOpenKey(null)} />
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* your people */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #FCF6EE, #F9F0E4)" }} data-testid="relations-map">
        <span className="absolute -left-24 top-10 w-[32vw] h-[32vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,154,79,0.26), transparent 68%)", filter: "blur(38px)" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-xl mb-9">
            <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(51,37,26,0.4)" }}>Your people, mapped</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2" style={{ color: INK }}>Drift is invisible until you draw it</h2>
            <p className="text-[15px] mt-3" style={{ color: SOFT }}>Private, on your device, never shared and never scored. Put people in a ring and move them when something actually happens.</p>
          </div>
          <PeopleMap
            people={data.people}
            onAdd={(p) => patch((d) => ({ people: [...(d.people || []), p].slice(0, 24) }))}
            onMove={(id, ring) => patch((d) => ({ people: (d.people || []).map((x) => (x.id === id ? { ...x, ring } : x)) }))}
            onRemove={(id) => patch((d) => ({ people: (d.people || []).filter((x) => x.id !== id) }))}
          />
        </div>
      </section>

      <ScriptsBand />
      <Weekly log={data.weekly} onLog={(t) => patch((d) => ({ weekly: [...(d.weekly || []), t] }))} />
      {!s && <Lonely />}

      <section style={{ background: PAPER }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="rounded-[2rem] p-8 sm:p-10 text-white relative overflow-hidden grain" style={{ background: "linear-gradient(140deg, #E89A4F, #A5551A)" }} data-testid="relations-room">
            <span className="absolute -right-16 -top-16 w-56 h-56 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.32), transparent 70%)" }} />
            <div className="flex items-start gap-4 relative">
              <Users size={22} className="mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl sm:text-3xl">A room per situation, so you land mid sentence</h2>
                <p className="mt-2.5 max-w-xl text-[15px] opacity-90">No introductions, no explaining the backstory. Everybody in the room arrived through the same sentence you did.</p>
                <p className="mt-4 text-[12px] opacity-75">Opens with the full platform. Your alias already works here.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Asked />

      <section style={{ background: "#F5EADD", borderTop: "1px solid rgba(51,37,26,0.07)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "rgba(51,37,26,0.4)" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "relationships").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "#fff", border: "1px solid rgba(51,37,26,0.08)", color: INK }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
